import _ from 'lodash';
import {
  RecruitmentActivityType,
  RecruitmentEntityType,
  RecruitmentProfileRequirementType,
  RequirementDataType,
  TBaseRecruitmentProfile,
  TRecruitmentRequirement
} from '../../../types/recruitmentTypes';
import { t } from 'i18next';
import { DAYS } from '../../../common/recruitment';
import { formatTimeRange, plural } from '../../libs/utils';
import { SCHEDULE_TYPES } from './constants';
import { safeCharacterName } from '../../../common/utils';
import Numeral from 'numeral';
import { TBaseRaidMetadata } from '../../../gameConfig/sharedTypes';

/*
 * takes in base profile state and a saved profile, translates the saved profile
 * into the model recognized by the UI.
 *
 * ideally, this is removed - and the ui + api both identify and work with identical
 * objects. the two were implemented a bit separately, so i will try to revisit this
 * at a later point in time
 */
const serializeCharacterProfile = (initialState: any, profile: any) => {
  return {
    ...initialState,
    ...profile.profile,
    ..._.omit(profile, 'profile'),
    privacyMode: profile.privacy_mode,
    timezone: profile.schedule_timezone,
    scheduleType: profile.schedule.type,
    schedule: profile.schedule.days,
    tags: _.map(profile.tags, (tag) => ({ ...tag, label: tag.name, value: tag.slug }))
  };
};

const serializeGuildProfile = serializeCharacterProfile;

export const ACTIVITY_OPTS = [
  { label: 'Raiding', value: RecruitmentActivityType.GuildRaids },
  { label: 'Guild PVP', value: RecruitmentActivityType.GuildPvp },
  { label: 'Guild Social', value: RecruitmentActivityType.GuildSocial }
];

export const getCommittedHours = (profile: TBaseRecruitmentProfile) => {
  if (!profile) {
    return null;
  }
  const { commitment_hours_min, commitment_hours_max } = profile;

  if (commitment_hours_min !== null && commitment_hours_max !== null) {
    if (commitment_hours_min === commitment_hours_max) {
      if (commitment_hours_min === 0) {
        return null;
      }
      return [commitment_hours_min];
    } else {
      return [commitment_hours_min, commitment_hours_max];
    }
  }
  return null;
};

export const getCommittedDays = (profile: TBaseRecruitmentProfile) => {
  if (!profile) {
    return null;
  }

  const { commitment_days_min, commitment_days_max } = profile;

  if (commitment_days_min !== null && commitment_days_max !== null) {
    if (commitment_days_min === commitment_days_max) {
      if (commitment_days_min === 0) {
        return null;
      }
      return [commitment_days_min];
    } else {
      return [commitment_days_min, commitment_days_max];
    }
  }
  return null;
};
export const serializeRecruitmentProfile = (initialState: any, profile: any) => {
  if (profile.profile.entity_type === RecruitmentEntityType.Character) {
    return serializeCharacterProfile(initialState, profile);
  } else if (profile.profile.entity_type === RecruitmentEntityType.Guild) {
    return serializeGuildProfile(initialState, profile);
  } else if (profile.profile.entity_type === RecruitmentEntityType.Team) {
    return serializeGuildProfile(initialState, profile);
  }
};

// extracts schedule summary from (ui) profile object for display purposes on
// preview card and char/guild recruitment page cards
export const getScheduleSummary = (profile: TBaseRecruitmentProfile) => {
  const { schedule, scheduleType } = profile;

  let days = '';
  let times = '';

  if (_.isEmpty(schedule) || !scheduleType) {
    return null;
  }

  const { start_minutes, end_minutes } = schedule[0];
  if (start_minutes === undefined || end_minutes === undefined) {
    return null;
  }

  if (scheduleType !== 'custom') {
    days = _.find(SCHEDULE_TYPES, { value: scheduleType })?.label || '-';
    times = formatTimeRange(start_minutes, end_minutes);
  } else {
    days = _.reduce(
      DAYS,
      (acc: any[], dayInfo, key) => {
        const scheduledDay = _.find(schedule, { day_of_week: Number(key) });
        if (scheduledDay) {
          acc.push(t(`recruitment:days-of-week.${dayInfo.slug}.abbreviation`));
        }
        return acc;
      },
      []
    ).join('/');

    times = formatTimeRange(start_minutes, end_minutes);

    const startTimes = _.uniqBy(schedule, 'start_minutes');
    const endTimes = _.uniqBy(schedule, 'end_minutes');
    if (startTimes.length > 1 || endTimes.length > 1) {
      times = t('recruitment:schedule.custom-time');
    }
  }

  return { times, days };
};

export const getProfileTarget = (profile: TBaseRecruitmentProfile & { [key: string]: any }): any => {
  const profileType = profile.entity_type;
  let target = {};

  if (profileType === RecruitmentEntityType.Character && 'characters' in profile) {
    target = _.find(profile.characters, { ordinal: 0 })?.character;
  }

  if (profileType === RecruitmentEntityType.Guild && 'guild' in profile) {
    target = profile.guild?.guild;
  }

  if (profileType === RecruitmentEntityType.Team && 'groupCharter' in profile) {
    target = profile.groupCharter.groupCharter;
  }

  return target;
};

// retrieves the name of the entity for a recruitment profile:
// guild name, group charter name, or main character name
const profileTypeNamePaths: { [key: string]: string } = {
  [RecruitmentEntityType.Team]: 'groupCharter.groupCharter.name',
  [RecruitmentEntityType.Guild]: 'guild.guild.name'
};

export const getBaseRecruitmentProfileName = (p: any): string => {
  if (p.entity_type === RecruitmentEntityType.Character) {
    return safeCharacterName(_.find(p.characters, { ordinal: 0 })?.character?.name || '');
  } else {
    return _.get(p, profileTypeNamePaths[p.entity_type], 'N/A');
  }
};

/* returns the classname color string of either
 * class (character profile) or
 * faction (guild/team profiles)
 */
export const getProfileEntityColorClassname = (p: TBaseRecruitmentProfile & { [key: string]: any }): string => {
  const target = getProfileTarget(p);

  if (_.isEmpty(target)) {
    return '';
  }

  return p.entity_type === RecruitmentEntityType.Character
    ? `class-color--${target.class.id}`
    : `rio-faction-color--${target.faction}`;
};

export const getCharacterProfileMainSpec = (p: TBaseRecruitmentProfile & { [key: string]: any }, specs: any) => {
  const mainChar = _.find(p.characters, { ordinal: 0 })!;
  if (mainChar) {
    return _.find(specs, { slug: mainChar.character.spec.slug })!;
  }
};
type TRequirementDiplay = {
  label: string;
  value: string;
};

const buildCharacterScheduleHighlights = (profile: TBaseRecruitmentProfile) => {
  const [committedDays, committedDaysMax] = getCommittedDays(profile) || [];
  const [committedHours, committedHoursMax] = getCommittedHours(profile) || [];
  // const { times, days } = getScheduleSummary(profile) || {};
  return [
    // comment this out here as we inject it later on so we can have a better-looking widget
    // days ? { label: days, value: times, isSchedule: true } : { label: 'Schedule', value: 'N/A' },
    {
      label: `${plural('Day', committedDays || 0)}/Week`,
      value: `${committedDays}${committedDaysMax ? '-' + committedDaysMax : ''}`
    },
    {
      label: `${plural('Hour', committedHours || 0)}/Day`,
      value: `${committedHours}${committedHoursMax ? '-' + committedHoursMax : ''}`
    }
  ];
};
// note: guild/team likely to have differnt validations, hence this additional
// method
export const getScheduleHighlights = (profile: TBaseRecruitmentProfile) => {
  switch (profile.entity_type) {
    case RecruitmentEntityType.Character:
    case RecruitmentEntityType.Team:
    case RecruitmentEntityType.Guild: {
      return buildCharacterScheduleHighlights(profile);
    }
    default: {
      return [];
    }
  }
};
// generated a summarized requirement list for the collapsed recruitment card view

export const summarizeHighlights = (reqs: any[], cap: number = 2) => {
  const summarized = _.compact([
    ...(reqs.length <= cap ? reqs.slice(0, cap) : []),
    ...(reqs.length > cap ? [...reqs.slice(0, cap - 1), { label: 'More', value: `+${reqs.length - 1}` }] : [])
  ]);
  return summarized;
};

export class RequirementDisplay {
  private raidMetadataBySlug: Record<string, TBaseRaidMetadata>;

  constructor(raidMetadataBySlug: Record<string, TBaseRaidMetadata>) {
    this.raidMetadataBySlug = raidMetadataBySlug || {};
  }

  private getRankTypeCategory(requirement: TRecruitmentRequirement): string {
    const [, rankType] = requirement.category.split(' ');
    return rankType || '';
  }

  private getRequirementValueBase(req: TRecruitmentRequirement): string {
    if (req.data_type === RequirementDataType.BOOLEAN) {
      return req.min_value !== 0 ? 'Must Have' : 'Must Not Have';
    } else if (req.data_type === RequirementDataType.INTEGER) {
      return req.min_value!.toString();
    } else if (req.data_type === RequirementDataType.INTEGER_RANGE) {
      if (!_.isNil(req.max_value)) {
        return `${req.min_value}-${req.max_value}`;
      }

      return req.min_value!.toString();
    }

    return req.min_value!.toString();
  }

  private getRequirementLabelBase(req: TRecruitmentRequirement): string {
    if (req.raid) {
      const raid = this.raidMetadataBySlug[req.raid];
      if (raid) {
        return `${req.name} ${raid.short_name}`;
      }
    }

    return req.name;
  }

  public getRequirementHighlights = (profile: TBaseRecruitmentProfile) => {
    return profile.requirements.reduce(
      (formattedRequirements: TRequirementDiplay[], requirement: TRecruitmentRequirement) => {
        if (this.isRequirementComplete(requirement)) {
          formattedRequirements.push(this.buildDisplay(requirement));
        }
        return formattedRequirements;
      },
      []
    );
  }

  private getGuildRankValue(requirement: TRecruitmentRequirement): string {
    const rankType = this.getRankTypeCategory(requirement);

    const numericalDisplayValue = !_.isNil(requirement.max_value)
      ? `${Numeral(requirement.min_value).format('Oo')}-${Numeral(requirement.max_value).format('Oo')}`
      : Numeral(requirement.min_value!).format('Oo');

    return `${rankType} ${numericalDisplayValue}`;
  }

  private getRequirementLabel(requirement: TRecruitmentRequirement) {
    switch (requirement.type) {
      case RecruitmentProfileRequirementType.Progress: {
        const raid = this.raidMetadataBySlug[requirement.raid];
        if (!raid) {
          return '';
        }
        return `${t(`raids:${requirement.raid}.short-name`)} ${t('common:terms.progress')}`;
      }
      case RecruitmentProfileRequirementType.GuildRank: {
        const raid = this.raidMetadataBySlug[requirement.raid];
        if (!raid) {
          return '';
        }
        return `${t(`raids:${requirement.raid}.short-name`)} ${t('common:terms.rank')} (${requirement.name[0]})`;
      }
      default: {
        return this.getRequirementLabelBase(requirement);
      }
    }
  }
  private getRequirementValue(requirement: TRecruitmentRequirement) {
    switch (requirement.type) {
      case RecruitmentProfileRequirementType.GuildRank: {
        return this.getGuildRankValue(requirement);
      }
      case RecruitmentProfileRequirementType.Progress: {
        const baseValue = this.getRequirementValueBase(requirement);
        const maxEncounters = requirement.data_max_value;
        return `${baseValue}/${maxEncounters} ${requirement.name[0]}`;
      }
      default: {
        return this.getRequirementValueBase(requirement);
      }
    }
  }

  public isRequirementComplete = (requirement: TRecruitmentRequirement): boolean => {
    return typeof requirement.min_value === 'number';
  }

  public buildDisplay = (requirement: TRecruitmentRequirement) => {
    return {
      label: this.getRequirementLabel(requirement),
      value: this.getRequirementValue(requirement)
    };
  }
}

/*
 * returns the faction string for a profile
 */
export const getProfileFaction = (p: TBaseRecruitmentProfile): string => {
  if (p.entity_type === RecruitmentEntityType.Character) {
    const mainChar = _.find((p as any).characters, { ordinal: 0 })!;
    return mainChar.character.faction;
  } else {
    const target = getProfileTarget(p);
    return target.faction;
  }
};

export const getRecruitmentProfileName = (p: any): string => {
  if (p.entity_type === RecruitmentEntityType.Character) {
    return safeCharacterName(_.find(p.characters, { ordinal: 0 })?.character?.name || '');
  } else {
    return _.get(p, profileTypeNamePaths[p.entity_type], 'N/A');
  }
};
